import { computed, nextTick, unref } from 'vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';
import {
  FOLDER_FILTER_PREFIX,
  folderIconPatch,
} from 'dashboard/components/widgets/conversation/conversationQuickFilters.js';

// Costura entre o drawer de filtros e as pastas da lista de conversas: qual
// pasta o editor altera (a escolhida nos Atalhos ou a aberta), onde grava o
// ícone e qual pasta vai para a exclusão. Fica fora do ChatList porque o
// ChatList inteiro não monta no jsdom, e um erro aqui mexe na pasta errada.
export function useChatListFolderEditor({
  // estado que o ChatList mantém (refs)
  folders,
  activeFolder,
  hasActiveFolders,
  showAdvancedFilters,
  filterDrawerShortcutMode,
  folderPendingEdit,
  folderPendingDelete,
  showDeleteFoldersModal,
  appliedFilter,
  isSaving,
  quickFiltersRaw,
  quickFilterIcons,
  // efeitos
  store,
  persistQuickFilters,
  loadFolderIntoFilter,
  notify,
}) {
  // Pasta que o editor do drawer altera: a escolhida nos Atalhos ou a aberta
  const folderBeingEdited = computed(() => {
    if (folderPendingEdit.value) return folderPendingEdit.value;
    return unref(hasActiveFolders) ? unref(activeFolder) : undefined;
  });

  const isEditingFolderInDrawer = computed(
    () => Boolean(folderBeingEdited.value) && !filterDrawerShortcutMode.value
  );

  const folderBeingEditedIcon = computed(() => {
    const folder = folderBeingEdited.value;
    if (!folder) return '';
    const folderKey = `${FOLDER_FILTER_PREFIX}${folder.id}`;
    const saved = (unref(quickFilterIcons) || {})[folderKey];
    return resolveSavedIconClass(saved || 'i-ph-folder');
  });

  // Aviso de nome repetido compara só com as outras pastas
  const otherFolderNames = computed(() => {
    const editingId = isEditingFolderInDrawer.value
      ? Number(folderBeingEdited.value.id)
      : null;
    return (unref(folders) || [])
      .filter(view => Number(view.id) !== editingId)
      .map(view => view.name);
  });

  const folderToDelete = computed(
    () => folderPendingDelete.value || unref(activeFolder)
  );

  const findFolder = folderId => {
    const id = Number(folderId);
    return (unref(folders) || []).find(view => Number(view.id) === id);
  };

  function closeAdvanceFiltersModal() {
    showAdvancedFilters.value = false;
    filterDrawerShortcutMode.value = false;
    folderPendingEdit.value = null;
    appliedFilter.value = [];
  }

  // O ícone da pasta vive nos Atalhos (uiSettings), não na custom view
  function persistFolderIcon(folderId, icon) {
    const patch = folderIconPatch(unref(quickFiltersRaw), folderId, icon);
    if (patch) persistQuickFilters(patch);
  }

  async function onUpdateSavedFilter(payload, folderName, icon) {
    if (unref(isSaving)) return;
    // Guarda a pasta antes de fechar: fechar zera a escolhida nos Atalhos
    const folder = unref(folderBeingEdited);
    if (!folder) return;
    const payloadData = {
      ...folder,
      name: unref(folderName),
      query: filterQueryGenerator(useSnakeCase(payload)),
    };
    try {
      await store.dispatch('customViews/update', payloadData);
      persistFolderIcon(folder.id, icon);
      closeAdvanceFiltersModal();
      notify('FILTER.CUSTOM_VIEWS.EDIT.SUCCESS_MESSAGE');
    } catch {
      notify('FILTER.CUSTOM_VIEWS.EDIT.ERROR_MESSAGE');
    }
  }

  // Excluir saiu do cabeçalho (ficava colado no lápis): agora vem do editor
  function onDeleteFolderFromDrawer() {
    const folder = folderBeingEdited.value;
    closeAdvanceFiltersModal();
    if (!folder) return;
    folderPendingDelete.value = folder;
    showDeleteFoldersModal.value = true;
  }

  // Editar pelos Atalhos abre o mesmo editor da pasta, sem precisar abri-la
  async function onEditCustomFilter(folderId) {
    const folder = findFolder(folderId);
    if (!folder) return;
    // O clique no painel já pode ter fechado o drawer (clique fora). Fecha e
    // espera desmontar, para o editor novo nascer com o nome e o ícone certos.
    closeAdvanceFiltersModal();
    await nextTick();
    filterDrawerShortcutMode.value = false;
    folderPendingEdit.value = folder;
    appliedFilter.value = [];
    loadFolderIntoFilter(folder);
    showAdvancedFilters.value = true;
  }

  return {
    folderBeingEdited,
    isEditingFolderInDrawer,
    folderBeingEditedIcon,
    otherFolderNames,
    folderToDelete,
    findFolder,
    closeAdvanceFiltersModal,
    onUpdateSavedFilter,
    onDeleteFolderFromDrawer,
    onEditCustomFilter,
  };
}
